'use client'

import type { LogContext, Logger } from '../logger/types'

import { createContext, useContext, useMemo } from 'react'

import { withContext } from '../logger/with-context'

/** Install form: provide a concrete logger (app root, dev harnesses, tests). */
interface LoggerProviderInstallProps {
  readonly children: React.ReactNode
  readonly logger: Logger
}

/** Derive form: wrap the ancestor logger with an owning team (and optionally extra context). */
interface LoggerProviderTeamScopeProps {
  readonly children: React.ReactNode
  /**
   * Owning team slug (e.g. `'fe-trading'`) merged into every log emitted
   * under this provider. Always wins over a `team` key in `context`; when
   * omitted, an ancestor's `team` (if any) is preserved.
   */
  readonly team: string
  /**
   * Extra scoped fields (e.g. `{ module: 'blotter' }`). MUST be
   * referentially stable — pass a module-level constant, or the derived
   * logger's identity churns every render and re-triggers downstream
   * effects.
   */
  readonly context?: LogContext
}

/** Derive form: wrap the ancestor logger with extra context (and optionally a team). */
interface LoggerProviderContextScopeProps {
  readonly children: React.ReactNode
  readonly team?: string
  readonly context: LogContext
}

type LoggerProviderProps =
  | LoggerProviderInstallProps
  | LoggerProviderTeamScopeProps
  | LoggerProviderContextScopeProps

const LoggerContext = createContext<Logger | null>(null)

/**
 * Provides a {@link Logger} to the component tree. There is exactly one
 * ambient logger context; this component has two forms over it:
 *
 * **Install** — pass `logger` to provide a concrete instance. Used once at
 * the app root (and in dev harnesses/tests). Unchanged, fully
 * backward-compatible form.
 *
 * **Derive** — pass `team` and/or `context` to wrap the nearest ancestor
 * logger with scoped fields via `withContext`. Used at feature roots so
 * every log beneath (including `ObservabilityProfiler` `slow_render` and
 * `ObservabilityErrorBoundary` `react_error` events) carries the scope.
 * `team` always wins over `context.team`; nested derive providers compose,
 * inner keys taking precedence. Throws if no ancestor logger exists.
 *
 * @example
 * ```tsx
 * // App root — install
 * <LoggerProvider logger={globalLogger}>
 *   <App />
 * </LoggerProvider>
 *
 * // Feature root — derive (team from the package's generated constant)
 * <LoggerProvider team={PACKAGE_TEAM} context={MODULE_CONTEXT}>
 *   <Feature />
 * </LoggerProvider>
 * ```
 */
export function LoggerProvider(props: LoggerProviderProps) {
  const parent = useContext(LoggerContext)

  const isInstallForm = 'logger' in props
  const installedLogger = isInstallForm ? props.logger : null
  const team = isInstallForm ? undefined : props.team
  const context = isInstallForm ? undefined : props.context

  const logger = useMemo(() => {
    if (installedLogger !== null) {
      return installedLogger
    }

    if (parent === null) {
      throw new Error(
        'LoggerProvider with team/context derives from an ancestor logger — mount a LoggerProvider with a logger above it',
      )
    }

    // Omit the team key entirely when unset — an explicit `team: undefined`
    // would clobber an ancestor's `team` in the withContext merge.
    const teamContext = team === undefined ? {} : { team }

    return withContext(parent, { ...context, ...teamContext })
  }, [installedLogger, parent, team, context])

  return (
    <LoggerContext.Provider value={logger}>
      {props.children}
    </LoggerContext.Provider>
  )
}

/**
 * Access the nearest {@link Logger} from a parent `LoggerProvider`.
 * Throws if called outside a provider.
 *
 * @example
 * ```tsx
 * function Dashboard() {
 *   const logger = useLogger()
 *   logger.info('Dashboard mounted')
 *   return <div>...</div>
 * }
 * ```
 */
export function useLogger(): Logger {
  const logger = useContext(LoggerContext)

  if (!logger) {
    throw new Error('useLogger must be used within a LoggerProvider')
  }

  return logger
}
